import { useLabelsMetadataStore } from '@aibees/elephant/store/labels-metadata';
import { PaginationRequest } from '@aibees/shared/definition';
import { useDebounceFn } from '@aibees/shared/hooks';
import { QueryOptions, useBaseQuery } from '@aibees/shared/http';
import { useWorkspace } from '@aibees/shared/workspace';
import { isEqual } from 'lodash-es';
import { useCallback } from 'react';
import { LABEL_API_PREFIX } from '../../constants';
import {
  BrowseMetadataResponse,
  LabelGrouping,
  LabelType,
  LabelVisibility,
} from '../../types/label';
import { LabelUpdatesSocketMessage } from './types';
import { useSocketLabelUpdateListener } from './use-socket-label-update-listener';

type BrowseLabelRequest = PaginationRequest<{
  grouping?: LabelGrouping;
  type?: LabelType;
  visibility?: LabelVisibility;
  name?: string;
}>;

let globalDebounceTimeout: NodeJS.Timeout | null = null;
// Module-level cache to track previous metadata across all hook instances
let cachedMetadata: BrowseMetadataResponse['metadata'] | undefined;

export function useBrowseLabel(
  params: BrowseLabelRequest,
  options?: QueryOptions
) {
  params.page = params.page || 0;
  params.size = params.size || 10;
  const { type, visibility, grouping, name, page, size, sort } = params;

  const setMetadata = useLabelsMetadataStore((state) => state.setMetadata);
  const workspace = useWorkspace();

  const api = useBaseQuery<typeof params, BrowseMetadataResponse>(
    [useBrowseLabel.name, type, grouping, visibility, name, sort, page, size],
    {
      url: LABEL_API_PREFIX.replace(':wid', String(workspace?.id)),
      params: {
        grouping,
        type,
        visibility,
        name,
        page,
        size,
        sort,
      },
    },
    {
      ...options,
      select: useCallback(
        (data: BrowseMetadataResponse) => {
          // Only update metadata if it's actually different (module-level comparison).
          // Defer setMetadata so we don't update a subscriber (e.g. VisibilitySelect) during
          // another component's render (e.g. AdvanceTableContextProvider), which causes
          // "Cannot update a component while rendering a different component".
          if (!isEqual(cachedMetadata, data.metadata)) {
            const nextMetadata = data.metadata;
            cachedMetadata = nextMetadata;
            queueMicrotask(() => setMetadata(nextMetadata));
          }
          return data;
        },
        [setMetadata]
      ),
    }
  );

  const debouncedRefetch = useCallback(() => {
    if (globalDebounceTimeout) {
      clearTimeout(globalDebounceTimeout);
    }
    globalDebounceTimeout = setTimeout(() => {
      api.refetch();
      globalDebounceTimeout = null;
    }, 1000); // Adjust the delay as needed.
  }, [api]);

  const onLabelUpdateEvent = useDebounceFn(
    (_data: LabelUpdatesSocketMessage) => {
      debouncedRefetch();
    },
    1000
  );

  useSocketLabelUpdateListener(onLabelUpdateEvent);

  return api;
}
